import React from "react";
import { AlertTriangle, AlertCircle, Info } from "lucide-react";

/**
 * PnLTruthBanner — renders the P&L Truth Contract state at the top of the page.
 *
 * Contract: /app/memory/PNL_TRUTH_CONTRACT.md §7
 *
 *  INVALID        → red banner, "P&L cannot be trusted"
 *  NOT_ACTIONABLE → amber banner, directional-only mode
 *  DIRECTIONAL    → amber-subtle info strip
 *  VALID / null   → nothing rendered
 *
 * Also surfaces cash_mode="PARTIAL" and truth_contract blocking reasons so
 * operators see WHY the system is suppressing data. Backend is the source
 * of truth for all messages; this component only renders them.
 */

const PRETTY_REASON = {
  "total_cogs:NEGATIVE_COGS": "Negative COGS detected in inventory data",
  "total_cogs:COGS_NOT_ACTIONABLE": "COGS is proxy-only — not decision-grade",
  "total_cogs:UNMAPPED_PURCHASES_EXCEED_THRESHOLD": "More than 10% of purchases are unmapped",
  "gross_profit:COGS_INVALID": "Gross Profit suppressed — COGS invalid",
  "gross_margin:COGS_INVALID": "Gross Margin suppressed — COGS invalid",
  "gross_margin:IMPOSSIBLE_MARGIN": "Computed margin outside 0–100% band",
  "target_state:TARGET_NOT_CONFIGURED": "Monthly target not configured",
  "target_state:TARGET_AHEAD_BUT_FORECAST_AT_RISK":
    "Target pacing contradicts forecast — please re-validate inputs",
  "food_cost_pct:COGS_NOT_ACTIONABLE": "Food cost % hidden — inventory not decision-grade",
  "liquor_cost_pct:COGS_NOT_ACTIONABLE": "Liquor cost % hidden — inventory not decision-grade",
  "beverage_cost_pct:COGS_NOT_ACTIONABLE": "Beverage cost % hidden — inventory not decision-grade",
};

function prettifyReason(raw) {
  if (!raw) return null;
  // Strip category annotations like [food] to match the lookup keys.
  const normalized = raw.replace(/\[[^\]]+\]/g, "");
  return PRETTY_REASON[normalized] || raw;
}

const PnLTruthBanner = ({ truthContract, cashMode, cashModeReason }) => {
  const state = truthContract?.state;
  const rawReasons = truthContract?.blocking_reasons || [];
  const reasons = rawReasons
    .map(prettifyReason)
    .filter(Boolean)
    // Dedupe while preserving order
    .filter((r, i, arr) => arr.indexOf(r) === i)
    .slice(0, 4);

  const showCashBanner = cashMode === "PARTIAL";

  // Nothing to render when everything is green and cash is TREASURY-mode.
  if (!state || state === "VALID") {
    if (!showCashBanner) return null;
  }

  const config =
    state === "INVALID"
      ? {
          border: "border-rose-500/40",
          bg: "bg-rose-500/8",
          title: "P&L cannot be trusted",
          blurb: "Data inconsistency detected. Fix inputs to unlock insights.",
          Icon: AlertCircle,
          iconColor: "text-rose-400",
          titleColor: "text-rose-300",
          testid: "pnl-truth-banner-invalid",
        }
      : state === "NOT_ACTIONABLE"
      ? {
          border: "border-amber-500/40",
          bg: "bg-amber-500/8",
          title: "Directional only",
          blurb: "Data quality below decision threshold. Insights are suppressed.",
          Icon: AlertTriangle,
          iconColor: "text-amber-400",
          titleColor: "text-amber-300",
          testid: "pnl-truth-banner-not-actionable",
        }
      : state === "DIRECTIONAL"
      ? {
          border: "border-amber-500/25",
          bg: "bg-amber-500/5",
          title: "Directional estimate",
          blurb: "Some metrics are based on proxy data.",
          Icon: Info,
          iconColor: "text-amber-400",
          titleColor: "text-amber-200",
          testid: "pnl-truth-banner-directional",
        }
      : null;

  return (
    <div className="space-y-2">
      {config && (
        <div
          className={`px-4 py-3 rounded-lg border ${config.border} ${config.bg}`}
          data-testid={config.testid}
        >
          <div className="flex items-start gap-3">
            <config.Icon className={`h-5 w-5 mt-0.5 ${config.iconColor}`} />
            <div className="flex-1">
              <div className={`text-sm font-medium ${config.titleColor}`}>
                {config.title}
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">{config.blurb}</div>
              {reasons.length > 0 && (
                <ul
                  className="mt-2 space-y-1 text-xs text-zinc-400"
                  data-testid="pnl-truth-banner-reasons"
                >
                  {reasons.map((r, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className="text-zinc-600">•</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {showCashBanner && (
        <div
          className="px-4 py-2.5 rounded-lg border border-amber-500/25 bg-amber-500/5"
          data-testid="pnl-cash-partial-banner"
        >
          <div className="flex items-center gap-3">
            <Info className="h-4 w-4 text-amber-400" />
            <div className="text-xs text-amber-200">
              Cash view is based on current-month movement only —
              opening balances not configured.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PnLTruthBanner;
